import { decode } from './rowTransforms';
import type {
    CommitCompleteSiteInput,
    CommitGeographyInput,
    CommitSummary,
    DecodedNewRow,
    DecodedRow,
    DecodedUpdateRow,
    RowCommitState,
} from './types';
import type { PreviewSiteManifestResponse, SetActivityBody, UpdateSiteBody } from '../state/api/organisationEngineSlice';

/* ── Mutator surface (consumed by step definitions) ──────────── */

export type CommitMutators = {
    /** Create a whole new site — row, geography and activity — in one call.
     *  Returns the assigned `id`. The engine writes all three or none, so
     *  there is no partial outcome for a caller to clean up. */
    createCompleteSite: (input: CommitCompleteSiteInput) => Promise<{ id: string }>;
    /** Update an existing site's name / project code. */
    updateSite: (siteId: string, body: UpdateSiteBody) => Promise<void>;
    /** Create (or replace) a site's geography from a point input. The
     *  flat lat/lng shape gets wrapped into a GeoJSON Feature inside the
     *  mutator implementation (`useCommit.ts`). */
    createGeography: (siteId: string, input: CommitGeographyInput) => Promise<void>;
    /** Replace a site's current activity (ISIC code). */
    setActivity: (siteId: string, body: SetActivityBody) => Promise<void>;
};

/* ── Step harness ────────────────────────────────────────────── */

/**
 * One step in a row's pipeline. The harness runs it when `shouldRun` says so
 * and the prior step succeeded. `run` returns a partial state patch (e.g.
 * `{ siteId }` from the create step) so a later step can read it without
 * state-threading boilerplate.
 */
export type StepDef<R extends DecodedRow> = {
    shouldRun: (row: R) => boolean;
    run: (row: R, mutators: CommitMutators) => Promise<Partial<RowCommitState>>;
};

/**
 * Walks a row through its steps in order, stopping at the first failure.
 *
 * On success, `overallStatus` lands on `'created'` (new rows) or `'updated'`
 * (update rows), derived from the row's `status` so the orchestrator's
 * summary doesn't need a per-pipeline parameter.
 */
export const runRowPipeline = async <R extends DecodedRow>(
    row: R,
    initial: RowCommitState,
    steps: StepDef<R>[],
    mutators: CommitMutators,
    onUpdate: (state: RowCommitState) => void
): Promise<RowCommitState> => {
    let state = report({ ...initial, overallStatus: 'in-progress' }, onUpdate);

    for (const step of steps) {
        if (!step.shouldRun(row)) continue;
        try {
            state = { ...state, ...(await step.run(row, mutators)) };
        } catch (err) {
            return failRow(state, err, onUpdate);
        }
    }

    return report({ ...state, overallStatus: row.status === 'new' ? 'created' : 'updated' }, onUpdate);
};

/* ── Orchestrator ────────────────────────────────────────────── */

export type CommitOptions = {
    preview: PreviewSiteManifestResponse;
    mutators: CommitMutators;
    /** Stream of state transitions — the UI subscribes and re-renders per row. */
    onRowUpdate: (state: RowCommitState) => void;
    /** Pipelines for each row classification. Injected by the consumer so the
     *  runner stays engine-agnostic. */
    newRowSteps: StepDef<DecodedNewRow>[];
    updateRowSteps: StepDef<DecodedUpdateRow>[];
};

/**
 * Walks preview.rows, decodes each into a domain shape, dispatches to the
 * matching step pipeline, and aggregates the outcome counts.
 *
 * Unchanged rows are not in `preview.rows` — they're surfaced as a count
 * directly from `preview.summary.unchanged`.
 */
export const commitManifest = async (opts: CommitOptions): Promise<CommitSummary> => {
    const { preview, mutators, onRowUpdate, newRowSteps, updateRowSteps } = opts;

    const summary: CommitSummary = {
        created: 0,
        updated: 0,
        unchangedSkipped: preview.summary.unchanged,
        failed: 0,
    };

    for (const row of preview.rows) {
        const decoded = decode(row);
        if (!decoded) continue; // error rows

        const final =
            decoded.status === 'new'
                ? await runRowPipeline(decoded, freshState(decoded), newRowSteps, mutators, onRowUpdate)
                : await runRowPipeline(decoded, freshState(decoded), updateRowSteps, mutators, onRowUpdate);

        if (final.overallStatus === 'created') summary.created += 1;
        else if (final.overallStatus === 'updated') summary.updated += 1;
        else summary.failed += 1;
    }

    return summary;
};

/* ── Reporting helpers ───────────────────────────────────────── */

const freshState = (decoded: DecodedRow): RowCommitState => ({
    rowNumber: decoded.rowNumber,
    overallStatus: 'pending',
});

const failRow = (state: RowCommitState, err: unknown, onUpdate: (s: RowCommitState) => void): RowCommitState =>
    report({ ...state, overallStatus: 'failed', errorMessage: extractMessage(err) }, onUpdate);

const report = (state: RowCommitState, onUpdate: (s: RowCommitState) => void): RowCommitState => {
    onUpdate(state);
    return state;
};

const extractMessage = (err: unknown): string => {
    if (typeof err === 'object' && err !== null) {
        // `.data.error` = engine 4xx/5xx with JSON body; `.error` = RTK Query
        // FETCH_ERROR (network drop, no response); `.message` = thrown Error.
        const e = err as { data?: { error?: string }; error?: string; message?: string };
        return e.data?.error ?? e.error ?? e.message ?? 'Unknown error';
    }
    return 'Unknown error';
};
